const TEXT_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'json', 'jsonl', 'xml', 'yaml', 'yml', 'toml',
  'js', 'jsx', 'ts', 'tsx', 'vue', 'css', 'scss', 'less', 'html', 'htm',
  'py', 'php', 'java', 'kt', 'kts', 'c', 'h', 'cpp', 'hpp', 'cs', 'go',
  'rs', 'rb', 'sh', 'bash', 'ps1', 'sql', 'log', 'ini', 'conf', 'env'
]);

const PARSER_VERSION = '1';
const DOCUMENT_MODEL_VERSION = '2';
const MAX_PREVIEW_CHARS = 600;

function getExtension(name = '') {
  const cleanName = String(name).split('?')[0];
  const index = cleanName.lastIndexOf('.');
  return index >= 0 ? cleanName.slice(index + 1).toLowerCase() : '';
}

function normalizeText(value = '') {
  return String(value).replace(/\r\n?/g, '\n').replace(/\u0000/g, '');
}

function createLineOffsets(text) {
  const offsets = [0];
  for (let index = 0; index < text.length; index += 1) {
    if (text.charCodeAt(index) === 10) offsets.push(index + 1);
  }
  return new Uint32Array(offsets);
}

function createLineOffsetsAsync(text) {
  if (typeof Worker === 'undefined' || text.length < 1024 * 1024) {
    return Promise.resolve(createLineOffsets(text));
  }
  return new Promise((resolve) => {
    const workerSource = `self.onmessage=function(event){var text=event.data;var offsets=[0];for(var i=0;i<text.length;i+=1){if(text.charCodeAt(i)===10)offsets.push(i+1);}var result=new Uint32Array(offsets);self.postMessage(result,[result.buffer]);};`;
    const workerUrl = URL.createObjectURL(new Blob([workerSource], { type: 'application/javascript' }));
    const worker = new Worker(workerUrl);
    const cleanup = () => {
      worker.terminate();
      URL.revokeObjectURL(workerUrl);
    };
    worker.onmessage = event => {
      const offsets = event.data instanceof Uint32Array ? event.data : new Uint32Array(event.data);
      cleanup();
      resolve(offsets);
    };
    worker.onerror = () => {
      cleanup();
      resolve(createLineOffsets(text));
    };
    worker.postMessage(text);
  });
}

function getPreview(text) {
  return normalizeText(text).trim().slice(0, MAX_PREVIEW_CHARS);
}

function getCapabilities(kind) {
  if (kind === 'image') return ['inspect', 'preview', 'attach', 'request_full_file'];
  if (kind === 'spreadsheet' || kind === 'csv') return ['inspect', 'search', 'read_range', 'eval', 'transform', 'request_full_file'];
  if (kind === 'document') return ['inspect', 'search', 'read_pages', 'eval', 'extract', 'transform', 'request_full_file'];
  return ['inspect', 'search', 'read_lines', 'eval', 'transform', 'request_full_file'];
}

async function parseImage(file) {
  let width = 0;
  let height = 0;
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    width = bitmap.width;
    height = bitmap.height;
    bitmap.close();
  }
  return {
    kind: 'image',
    parser: 'browser-image',
    parserVersion: PARSER_VERSION,
    text: '',
    preview: `${file.type || 'image'}${width && height ? `, ${width} x ${height}` : ''}`,
    stats: { width, height },
    capabilities: getCapabilities('image'),
    indexes: { kind: 'image', width, height }
  };
}

async function parseText(file, kind = 'text') {
  const text = normalizeText(await file.text());
  const lineOffsets = await createLineOffsetsAsync(text);
  const rows = kind === 'csv' ? parseCsvRows(text) : null;
  return {
    kind,
    parser: kind === 'csv' ? 'browser-csv' : 'browser-text',
    parserVersion: PARSER_VERSION,
    text,
    preview: getPreview(text),
    stats: kind === 'csv'
      ? { lines: lineOffsets.length, rows: rows.length, columns: rows[0] ? rows[0].length : 0 }
      : { lines: lineOffsets.length },
    capabilities: getCapabilities(kind),
    indexes: { kind, text, lineOffsets, ...(rows ? { rows, columns: rows[0] || [] } : {}) }
  };
}

function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      row.push(cell.trim());
      cell = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += character;
    }
  }
  if (cell || row.length) {
    row.push(cell.trim());
    rows.push(row);
  }
  return rows;
}

async function parseSpreadsheet(file) {
  const buffer = await file.arrayBuffer();
  let sheets;
  if (typeof Worker !== 'undefined') {
    try {
      sheets = await new Promise((resolve, reject) => {
        const worker = new Worker(new URL('./workspaceParser.worker.js', import.meta.url));
        worker.onmessage = event => {
          worker.terminate();
          if (event.data && event.data.ok) resolve(event.data.sheets || []);
          else reject(new Error((event.data && event.data.error) || 'Spreadsheet parsing failed'));
        };
        worker.onerror = error => {
          worker.terminate();
          reject(error);
        };
        worker.postMessage({ type: 'xlsx', buffer });
      });
    } catch (error) {
      console.warn('Spreadsheet worker unavailable, parsing on the main thread:', error);
    }
  }
  if (!sheets) {
    const XLSX = await import(/* webpackChunkName: "workspace-xlsx" */ 'xlsx');
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    sheets = workbook.SheetNames.map(name => ({
      name,
      rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw: false, defval: '' })
    }));
  }
  const text = sheets.map(sheet => {
    const body = sheet.rows.map(row => row.map(cell => String(cell)).join('\t')).join('\n');
    return `[Sheet: ${sheet.name}]\n${body}`;
  }).join('\n\n');
  const lineOffsets = await createLineOffsetsAsync(text);
  return {
    kind: 'spreadsheet',
    parser: 'sheetjs',
    parserVersion: PARSER_VERSION,
    text,
    preview: getPreview(text),
    stats: {
      sheets: sheets.length,
      rows: sheets.reduce((total, sheet) => total + sheet.rows.length, 0),
      sheetNames: sheets.map(sheet => sheet.name)
    },
    capabilities: getCapabilities('spreadsheet'),
    indexes: { kind: 'spreadsheet', text, lineOffsets, sheets }
  };
}

async function parseDocx(file) {
  const mammothModule = await import(/* webpackChunkName: "workspace-docx" */ 'mammoth');
  const mammoth = mammothModule.default || mammothModule;
  const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
  const text = normalizeText(result.value || '');
  const lineOffsets = await createLineOffsetsAsync(text);
  const paragraphs = text.split(/\n{2,}/).map(value => value.trim()).filter(Boolean);
  return {
    kind: 'document',
    parser: 'mammoth',
    parserVersion: PARSER_VERSION,
    text,
    preview: getPreview(text),
    stats: { lines: lineOffsets.length, paragraphs: text.split(/\n{2,}/).filter(Boolean).length },
    capabilities: getCapabilities('document'),
    indexes: { kind: 'document', text, lineOffsets, paragraphs }
  };
}

async function parsePdf(file) {
  const pdfjs = await import(/* webpackChunkName: "workspace-pdf" */ 'pdfjs-dist/legacy/build/pdf.js');
  await import(/* webpackChunkName: "workspace-pdf-worker" */ 'pdfjs-dist/legacy/build/pdf.worker.min.js');
  const loadingTask = pdfjs.getDocument({ data: await file.arrayBuffer(), disableWorker: true });
  const pdf = await loadingTask.promise;
  const pages = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    pages.push(content.items.map(item => item.str || '').join(' '));
  }
  const text = pages.map((page, index) => `[Page ${index + 1}]\n${page}`).join('\n\n');
  const lineOffsets = await createLineOffsetsAsync(text);
  return {
    kind: 'document',
    parser: 'pdfjs',
    parserVersion: PARSER_VERSION,
    text,
    preview: getPreview(text),
    stats: { pages: pages.length },
    capabilities: getCapabilities('document'),
    indexes: { kind: 'document', text, lineOffsets, pages }
  };
}

export function getWorkspaceFileSupport(file) {
  const extension = getExtension(file && file.name);
  const mime = String((file && file.type) || '').toLowerCase();
  if (mime.startsWith('image/')) return { supported: true, kind: 'image', extension };
  if (extension === 'doc' || extension === 'xls') {
    return { supported: false, reason: `暂不支持 .${extension}，请转存为 .${extension}x 后重试。`, extension };
  }
  if (extension === 'xlsx') return { supported: true, kind: 'spreadsheet', extension };
  if (extension === 'docx') return { supported: true, kind: 'document', extension };
  if (extension === 'pdf') return { supported: true, kind: 'document', extension };
  if (extension === 'csv') return { supported: true, kind: 'csv', extension };
  if (TEXT_EXTENSIONS.has(extension) || mime.startsWith('text/')) {
    return { supported: true, kind: 'text', extension };
  }
  return { supported: false, reason: `暂不支持 ${extension ? `.${extension}` : '该'} 文件类型。`, extension };
}

export async function parseWorkspaceFile(file, options = {}) {
  const support = getWorkspaceFileSupport(file);
  if (!support.supported) throw new Error(support.reason);
  if (options.signal && options.signal.aborted) throw new DOMException('Aborted', 'AbortError');
  if (options.onProgress) options.onProgress(5);

  let result;
  if (support.kind === 'image') result = await parseImage(file);
  else if (support.kind === 'spreadsheet') result = await parseSpreadsheet(file);
  else if (support.extension === 'docx') result = await parseDocx(file);
  else if (support.extension === 'pdf') result = await parsePdf(file);
  else result = await parseText(file, support.kind);

  if (options.signal && options.signal.aborted) throw new DOMException('Aborted', 'AbortError');
  if (options.onProgress) options.onProgress(100);
  const indexes = result.indexes || {};
  const model = {
    id: options.fileId || '',
    name: file.name || '',
    kind: result.kind,
    metadata: {
      mime: file.type || '',
      size: file.size || 0,
      extension: support.extension,
      parser: result.parser,
      parserVersion: result.parserVersion,
      documentModelVersion: DOCUMENT_MODEL_VERSION
    },
    text: result.text || '',
    lines: indexes.lineOffsets ? { count: indexes.lineOffsets.length } : [],
    rows: indexes.rows || (indexes.sheets && indexes.sheets[0] ? indexes.sheets[0].rows : []),
    sheets: indexes.sheets || [],
    pages: indexes.pages || [],
    paragraphs: indexes.paragraphs || [],
    tables: indexes.tables || [],
    image: result.kind === 'image' ? { width: result.stats.width || 0, height: result.stats.height || 0 } : null,
    capabilities: result.capabilities || []
  };
  return { ...result, documentModelVersion: DOCUMENT_MODEL_VERSION, model };
}

export { DOCUMENT_MODEL_VERSION, getExtension, createLineOffsets, parseCsvRows };
